import { CheckCircle, ArrowRight } from 'lucide-react';
import { WIZARD_STEPS, STEP_LABELS } from './drawdownCreateTypes';
import type { WizardStep } from './drawdownCreateTypes';

interface DrawdownCreateStepIndicatorProps {
  currentStep: WizardStep;
  canAccessStep: (step: WizardStep) => boolean;
  onStepClick: (step: WizardStep) => void;
}

function getStepCircleClassName(step: {
  isActive: boolean;
  isCompleted: boolean;
  canAccess: boolean;
}): string {
  if (step.isActive) return 'bg-blue-600 text-white';
  if (step.isCompleted) return 'bg-green-600 text-white';
  if (step.canAccess)
    return 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500';
}

export function DrawdownCreateStepIndicator({
  currentStep,
  canAccessStep,
  onStepClick,
}: DrawdownCreateStepIndicatorProps) {
  const currentIndex = WIZARD_STEPS.indexOf(currentStep);

  return (
    <div className="flex items-center">
      {WIZARD_STEPS.map((step, index) => {
        const isActive = step === currentStep;
        const isCompleted = index < currentIndex;
        const canAccess = canAccessStep(step);

        return (
          <div key={step} className="flex items-center">
            <div
              role={canAccess ? 'button' : undefined}
              tabIndex={canAccess ? 0 : undefined}
              className={`
                flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                ${getStepCircleClassName({ isActive, isCompleted, canAccess })}
              `}
              onClick={() => canAccess && onStepClick(step)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') &&
                canAccess &&
                onStepClick(step)
              }
            >
              {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
            </div>
            <span
              className={`ml-2 text-sm ${isActive ? 'font-medium' : 'text-gray-600 dark:text-gray-400'}`}
            >
              {STEP_LABELS[step]}
            </span>
            {index < WIZARD_STEPS.length - 1 && (
              <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500 mx-4" />
            )}
          </div>
        );
      })}
    </div>
  );
}
